import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export const SignUpContainer = styled.div`
  width: 100%;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const FormGroup = styled.div`
  position: relative;
`;

export const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const Input = styled.input`
  width: 100%;
  height: 48px;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: var(--control-radius, var(--card-radius));
  font-size: 0.95rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  background-color: var(--background-primary);
  color: var(--text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

export const Icon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--text-secondary);
    }
  }
`;

export const Button = styled.button`
  width: 100%;
  height: 48px;
  padding: 0.875rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: var(--control-radius, var(--card-radius));
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: filter 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      filter: brightness(0.95);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

// Soft Alert Banner
export const AlertBanner = styled(motion.div)`
  background: ${props => props.type === 'error' 
    ? 'rgba(220, 53, 69, 0.08)' 
    : 'rgba(56, 161, 105, 0.08)'};
  border: 1px solid ${props => props.type === 'error' 
    ? 'rgba(220, 53, 69, 0.25)' 
    : 'rgba(56, 161, 105, 0.25)'};
  border-radius: var(--card-radius);
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

export const AlertIcon = styled.div`
  color: ${props => props.type === 'error' 
    ? 'rgba(220, 53, 69, 0.9)' 
    : 'rgba(56, 161, 105, 0.9)'};
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
`;

export const AlertText = styled.div`
  color: ${props => props.type === 'error' 
    ? 'rgba(220, 53, 69, 0.95)' 
    : 'rgba(56, 161, 105, 0.95)'};
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.5;
  flex: 1;
`;

export const ErrorMessage = styled(AlertBanner).attrs({ type: 'error' })``;
export const SuccessMessage = styled(AlertBanner).attrs({ type: 'success' })``;

export const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: var(--border-color);
  }
  
  span {
    padding: 0 1rem;
    color: var(--text-muted);
    font-size: 0.875rem;
  }
`;

export const LoginLink = styled.div`
  text-align: center;
  font-size: 0.875rem;
  color: var(--text-secondary);
  
  a {
    color: var(--primary-color);
    text-decoration: none;
    font-weight: 500;
    
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        color: var(--primary-dark);
        text-decoration: underline;
      }
    }
  }
`;

export const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const PasswordRequirements = styled.div`
  background: var(--background-tertiary, var(--background-secondary));
  border: 1px solid var(--border-color);
  border-radius: var(--card-radius);
  padding: 0.75rem;
  margin-top: 0.5rem;
`;

export const Requirement = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-bottom: 0.25rem;

  &:last-child {
    margin-bottom: 0;
  }

  &.valid {
    color: var(--success-color);
  }

  &.invalid {
    color: var(--error-color);
  }

  &.info {
    color: var(--text-muted);
    font-size: 0.8rem;
  }
`;

export const RequirementIcon = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.valid ? 'var(--success-color)' : 'var(--error-color)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-inverse);
  font-size: 8px;
  font-weight: bold;
  
  ${props => props.children === '•' && `
    background: var(--text-muted);
    color: var(--background-primary);
    font-size: 10px;
    font-weight: bold;
  `}
`;

export const EmailMatchIndicator = styled.div`
  margin-top: 0.5rem;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.matches ? 'var(--success-color)' : 'var(--error-color)'};
`;

export const EmailExistsMessage = styled.div`
  margin-top: 0.5rem;
  padding: 0.75rem;
  background: rgba(26, 54, 93, 0.1);
  border: 1px solid rgba(26, 54, 93, 0.3);
  border-radius: var(--card-radius);
  font-size: 0.75rem;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

export const EmailLoginLink = styled(Link)`
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: 0.25rem;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--primary-dark);
      text-decoration: underline;
    }
  }
`;

export const Title = styled.h2`
  text-align: center;
  width: 100%;
  margin-bottom: 1.25rem;
  margin-top: 0;
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
`;

export const CheckingEmailText = styled.div`
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: var(--text-muted);
`;
